/**
 * Dates, at the house.
 *
 * Three features now need to agree on what day it is: the week (F7) has to draw
 * Monday to Sunday, Sunday (F11) has to know it is Sunday, and the daily line (F10)
 * already turned over at midnight in Pretoria rather than on the device. Each of
 * them had — or would have had — its own `Intl.DateTimeFormat("en-ZA", { timeZone:
 * "Africa/Johannesburg" })`, and three copies of a timezone decision is two copies
 * too many. `dailyline.ts` keeps its own because it is deliberately dependency-free
 * and renders before anything else on a tablet with no signal; everything since
 * comes through here.
 *
 * The unit throughout is a **house ISO date** — `"2026-08-16"`, the calendar day in
 * Pretoria. Not a `Date`, on purpose: a `Date` carries a time and an offset, and
 * every bug in this area comes from one of the two being the device's rather than
 * the house's. A string cannot drift.
 */
import { TZ } from "@steyncd/steyn-design";

/**
 * `formatToParts` rather than a locale that happens to print ISO order.
 *
 * `en-CA` produces "2026-08-16" today and that is a property of the locale data
 * shipped with the browser, not a guarantee. Assembling the parts by name is the
 * same length and cannot be changed out from under us by an ICU update.
 */
const PARTS = new Intl.DateTimeFormat("en-ZA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The calendar day in Pretoria, as `YYYY-MM-DD`. */
export function houseISO(at: Date | number = Date.now()): string {
  const p = PARTS.formatToParts(at);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "01";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

/**
 * Midday, at the house, on that date.
 *
 * South African Standard Time is a fixed +02:00 and has had no daylight saving
 * since 1944, so an ISO date plus a literal offset is an exact instant and needs no
 * library. Midday rather than midnight because it is the hour furthest from any
 * boundary — if that assumption ever stops holding, arithmetic here still lands on
 * the right calendar day instead of the one before it.
 */
export function houseNoon(iso: string): Date {
  return new Date(`${iso}T12:00:00+02:00`);
}

/** 0 = Sunday … 6 = Saturday, for the house's calendar day. */
export function dayOfWeek(iso: string): number {
  return houseNoon(iso).getUTCDay();
}

/** `n` days on or back, still as a house ISO date. */
export function addDays(iso: string, n: number): string {
  return houseISO(houseNoon(iso).getTime() + n * 86_400_000);
}

/**
 * The Monday of the house week containing `iso`.
 *
 * Monday-first, because that is what the week is for here: school runs, sport and
 * lifts are a Monday-to-Friday shape with a weekend attached, and a Sunday-first
 * grid puts the quiet day in the column the eye starts at.
 */
export function mondayOf(iso: string): string {
  const d = dayOfWeek(iso);
  return addDays(iso, d === 0 ? -6 : 1 - d);
}

/** True when it is Sunday at the house — F11's whole trigger. */
export function isSunday(at: Date | number = Date.now()): boolean {
  return dayOfWeek(houseISO(at)) === 0;
}

const DAY_NAME = new Intl.DateTimeFormat("en-ZA", { timeZone: TZ, weekday: "short" });
const DAY_NUM = new Intl.DateTimeFormat("en-ZA", { timeZone: TZ, day: "numeric" });
const MONTH = new Intl.DateTimeFormat("en-ZA", { timeZone: TZ, month: "short" });

/** "Mon" / "17" / "Aug" — the three pieces a day column heading is made of. */
export function dayLabel(iso: string): { name: string; num: string; month: string } {
  const at = houseNoon(iso);
  return { name: DAY_NAME.format(at), num: DAY_NUM.format(at), month: MONTH.format(at) };
}
